import { Paper, Typography, Box, CardActionArea, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import BlogAvatar from './BlogAvatar';
import TopicChips from './TopicChips';

// Full-bleed row. Clipped so the CardActionArea ripple respects the radius.
const BlogRow = styled(Paper)(({ theme }) => ({
  overflow: 'hidden',
  transition: theme.transitions.create(['box-shadow', 'transform'], {
    duration: theme.transitions.duration.shorter,
  }),
  '&:hover': {
    boxShadow: theme.shadows[10],
    transform: 'translateY(-2px)',
  },
})) as typeof Paper;

const RowAction = styled(CardActionArea)(({ theme }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: { padding: theme.spacing(3) },
})) as typeof CardActionArea;

// Fixed column for the topic pills so titles line up down the page instead of
// starting at a different x on every row.
const TopicColumn = styled(Box)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.up('md')]: {
    display: 'block',
    flex: '0 0 auto',
    width: 116,
  },
}));

// The same pills, shown inline above the title once the column is dropped.
const InlineTopics = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(0.75),
  [theme.breakpoints.up('md')]: { display: 'none' },
}));

// Clamp the description so every row is the same height regardless of how long
// the headline is. `-webkit-line-clamp` is supported everywhere we care about.
const Description = styled(Typography)({
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
});

type BlogProps = {
  id: string;
  name: string;
  topics: string[];
  headline: string;
  created: number;
  location: string;
  image?: string;
};

const Blog: React.FC<BlogProps> = (props) => {
  const createdDate = new Date(props.created).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <BlogRow elevation={4}>
      <RowAction component={RouterLink} to={`/blogs/${props.id}`}>
        <Stack direction="row" spacing={{ xs: 2, sm: 3 }} alignItems="flex-start">
          <BlogAvatar
            name={props.name}
            slug={props.location}
            image={props.image}
            size={72}
          />

          <TopicColumn>
            <TopicChips topics={props.topics} direction="column" />
          </TopicColumn>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <InlineTopics>
              <TopicChips topics={props.topics} />
            </InlineTopics>

            <Typography variant="h5" component="h2" sx={{ lineHeight: 1.25 }}>
              {props.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, mb: 1 }}>
              {createdDate}
            </Typography>
            <Description variant="body2" color="text.secondary">
              {props.headline}
            </Description>
          </Box>
        </Stack>
      </RowAction>
    </BlogRow>
  );
};

export default Blog;
